// ============================================================================
// The recipient USAddress -> ForeignAddress rewrite, with no file system access.
//
// sanitize/foreignAddress.ts resolves country names from the shipped .xsd, read off disk, so it
// cannot run in a browser. The rewrite itself is pure, and the FIRE converter needs it in the
// browser: its XML is the generator's output with no sanitizer pass, so a foreign payee would
// otherwise go out as a USAddress with a province in StateAbbreviationCd, which the schema
// refuses. Both callers share this; each passes its own country lookup.
//
// ForeignAddressType element order (per IRS-IRefileTypes.xsd):
//   AddressLine1Txt, AddressLine2Txt?, CityNm?, ProvinceOrStateNm?, CountryCd, ForeignPostalCd?
// ============================================================================

const tag = (xml: string, name: string): string | null => {
  const m = xml.match(new RegExp("<" + name + ">([^<]*)</" + name + ">"));
  return m ? m[1] : null;
};
const el = (name: string, val: string | null): string =>
  val && val.trim() ? "<" + name + ">" + val + "</" + name + ">" : "";

/**
 * Rewrite the recipient USAddress into a ForeignAddress for every recipient whose record
 * carries a FOREIGN_COUNTRY. Address lines / city are reused from the already-built USAddress;
 * the state becomes ProvinceOrStateNm, the ZIP becomes ForeignPostalCd, and CountryCd is the
 * IRS code. Recipients match records by order (one RecipientDetail each).
 */
export function rewriteForeignAddresses(
  xml: string,
  records: any[],
  toCountryCode: (raw: string) => string | null
): string {
  if (!xml || !records || records.length === 0) return xml;
  let i = 0;
  return xml.replace(/<RecipientDetail>([\s\S]*?)<\/RecipientDetail>/g, (full) => {
    const rec = records[i++];
    const country = String(rec?.FOREIGN_COUNTRY ?? "").trim();
    if (!country) return full; // US recipient — leave as USAddress

    const code = toCountryCode(country);
    return full.replace(/<USAddress>([\s\S]*?)<\/USAddress>/, (_m, inner) => {
      const addr1 = tag(inner, "AddressLine1Txt");
      const addr2 = tag(inner, "AddressLine2Txt");
      const city = tag(inner, "CityNm");
      const province = tag(inner, "StateAbbreviationCd");
      const postal = tag(inner, "ZIPCd");
      // If the country can't be resolved, emit it raw so the XSD reports a clear CountryCd
      // error (better than a misleading invalid-state error from leaving a USAddress).
      const cc = code ?? country.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();
      return (
        "<ForeignAddress>" +
        el("AddressLine1Txt", addr1 || "NA") +
        el("AddressLine2Txt", addr2) +
        el("CityNm", city) +
        el("ProvinceOrStateNm", province) +
        el("CountryCd", cc) +
        el("ForeignPostalCd", postal) +
        "</ForeignAddress>"
      );
    });
  });
}
